import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../utils/api';
import { getAllLicStates } from '../api/States';
import { getAllProfessions } from '../api/Professions';
import { getStateRequirement, IStateRequirement } from '../api/StateRequirements';
import { Profession } from '../api/Professions';
import LicenseForm from '../components/LicenseForm';
import { UserLicense, LicState } from '../types';

interface MemberInfo {
  memberId: string;
  userId: string;
  role: string;
  memberType: string;
  inviteStatus: string;
  inviteEmail?: string;
  user: {
    _id: string;
    firstName?: string;
    lastName?: string;
    email: string;
    professionID?: string;
    licenses?: UserLicense[];
  };
  activeCycles: number;
}

interface Cycle {
  _id: string;
  cycleid: number;
  cyclename?: string;
  professionID?: string;
  stateid?: number;
  cyclestart?: string;
  cycleend?: string;
}

const EMPTY_CYCLE_FORM = {
  cyclename: '',
  professionID: '',
  stateid: '',
  cyclestart: '',
  cycleend: '',
};

const TeamMemberManage: React.FC = () => {
  const { memberId } = useParams<{ memberId: string }>();
  const [member, setMember] = useState<MemberInfo | null>(null);
  const [cycles, setCycles] = useState<Cycle[]>([]);
  const [professions, setProfessions] = useState<Profession[]>([]);
  const [states, setStates] = useState<LicState[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [resending, setResending] = useState(false);

  // License management
  const [licenses, setLicenses] = useState<UserLicense[]>([]);
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [licenseMode, setLicenseMode] = useState<'add' | 'edit'>('add');
  const [editingLicense, setEditingLicense] = useState<UserLicense | null>(null);
  const [licenseLoading, setLicenseLoading] = useState(false);

  // Profession editor
  const [editingProfession, setEditingProfession] = useState(false);
  const [professionDraft, setProfessionDraft] = useState('');
  const [professionSaving, setProfessionSaving] = useState(false);

  // Add Cycle modal
  const [showAddCycle, setShowAddCycle] = useState(false);
  const [cycleForm, setCycleForm] = useState(EMPTY_CYCLE_FORM);
  const [cycleAdding, setCycleAdding] = useState(false);
  const [cycleError, setCycleError] = useState('');
  const [stateReq, setStateReq] = useState<IStateRequirement | null>(null);
  const reqCacheRef = useRef<Record<string, IStateRequirement | null>>({});

  const load = async () => {
    try {
      const [membersRes, cyclesRes] = await Promise.all([
        api.get('/api/company/members'),
        api.get(`/api/company/members/${memberId}/cycles`),
      ]);
      const found = membersRes.data.find((m: MemberInfo) => m.memberId === memberId);
      setMember(found || null);
      setLicenses(found?.user?.licenses || []);
      setCycles(cyclesRes.data);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to load member data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Load profession + state lists for the Add Cycle modal
    getAllProfessions()
      .then(ps => setProfessions(ps.filter((p: Profession) => p.isActive !== false)))
      .catch(() => {});
    getAllLicStates()
      .then(ss => setStates(ss.sort((a, b) => a.state.localeCompare(b.state))))
      .catch(() => {});
  }, [memberId]);

  const deleteCycle = async (cycleId: string, cycleName: string) => {
    if (!window.confirm(`Delete cycle "${cycleName}"? This will detach all classes from it.`)) return;
    setDeletingId(cycleId);
    try {
      await api.delete(`/api/company/members/${memberId}/cycles/${cycleId}`);
      setCycles(prev => prev.filter(c => c._id !== cycleId));
    } catch (e: any) {
      alert(e.response?.data?.error || 'Failed to delete cycle.');
    } finally {
      setDeletingId(null);
    }
  };

  const resendInvite = async () => {
    if (!member) return;
    setResending(true);
    try {
      await api.post(`/api/company/members/${memberId}/resend-invite`);
      alert(`Invite email sent to ${member.inviteEmail || member.user?.email}.`);
      await load();
    } catch (e: any) {
      alert(e.response?.data?.error || 'Failed to send invite.');
    } finally {
      setResending(false);
    }
  };

  const openProfessionEdit = () => {
    setProfessionDraft(member?.user?.professionID || licenses[0]?.professionID || '');
    setEditingProfession(true);
  };

  const saveProfession = async () => {
    if (!professionDraft) return;
    setProfessionSaving(true);
    try {
      await api.patch(`/api/company/members/${memberId}/profession`, { professionID: professionDraft });
      setMember(prev => prev ? { ...prev, user: { ...prev.user, professionID: professionDraft.toUpperCase() } } : prev);
      setEditingProfession(false);
    } catch (e: any) {
      alert(e.response?.data?.error || 'Failed to update profession.');
    } finally {
      setProfessionSaving(false);
    }
  };

  const openAddLicense = () => {
    setLicenseMode('add');
    setEditingLicense(null);
    setShowLicenseModal(true);
  };

  const openEditLicense = (lic: UserLicense) => {
    setLicenseMode('edit');
    setEditingLicense(lic);
    setShowLicenseModal(true);
  };

  const handleLicenseSubmit = async (data: Partial<UserLicense>) => {
    setLicenseLoading(true);
    try {
      if (licenseMode === 'add') {
        const res = await api.post(`/api/company/members/${memberId}/licenses`, data);
        setLicenses(prev => [...prev, res.data]);
      } else if (editingLicense?._id) {
        const res = await api.put(`/api/company/members/${memberId}/licenses/${editingLicense._id}`, data);
        setLicenses(prev => prev.map(l => l._id === editingLicense._id ? res.data : l));
      }
      setShowLicenseModal(false);
    } catch (e: any) {
      alert(e.response?.data?.error || 'Failed to save license.');
    } finally {
      setLicenseLoading(false);
    }
  };

  const deleteLicense = async (lic: UserLicense) => {
    if (!lic._id) return;
    if (!window.confirm(`Delete ${lic.professionID} license #${lic.licenseNumber}?`)) return;
    try {
      await api.delete(`/api/company/members/${memberId}/licenses/${lic._id}`);
      setLicenses(prev => prev.filter(l => l._id !== lic._id));
      setShowLicenseModal(false);
    } catch (e: any) {
      alert(e.response?.data?.error || 'Failed to delete license.');
    }
  };

  const openAddCycle = () => {
    const prof = member?.user?.professionID || '';
    setStateReq(null);
    setCycleForm({ ...EMPTY_CYCLE_FORM, professionID: prof });
    setCycleError('');
    setShowAddCycle(true);
  };

  /** Fetch + cache state requirement when both profession and state are known. */
  const fetchRequirement = async (professionID: string, stateid: string) => {
    if (!professionID || !stateid) { setStateReq(null); return; }
    const key = `${professionID}:${stateid}`;
    if (key in reqCacheRef.current) {
      setStateReq(reqCacheRef.current[key]);
      return;
    }
    try {
      const req = await getStateRequirement(professionID, Number(stateid));
      reqCacheRef.current[key] = req;
      setStateReq(req);
    } catch {
      reqCacheRef.current[key] = null;
      setStateReq(null);
    }
  };

  /** Compute end date from a start date and cycle length in years (end = start + years - 1 day). */
  const computeEndDate = (start: string, years: number): string => {
    if (!start || !years) return '';
    const [y, m, d] = start.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    date.setFullYear(date.getFullYear() + years);
    date.setDate(date.getDate() - 1);
    return date.toISOString().split('T')[0];
  };

  /** Build an auto-name from profession + state abbreviation + year. */
  const buildCycleName = (professionID: string, stateid: string, startDate: string): string => {
    const stateObj = states.find(s => String(s.stateID) === stateid);
    const year = startDate ? startDate.slice(0, 4) : new Date().getFullYear().toString();
    if (!professionID || !stateObj) return '';
    return `${professionID}-${stateObj.state}-${year}`;
  };

  const handleCycleProfessionChange = async (professionID: string) => {
    const { stateid, cyclestart } = cycleForm;
    const req = stateid ? await (async () => {
      await fetchRequirement(professionID, stateid);
      return reqCacheRef.current[`${professionID}:${stateid}`] ?? null;
    })() : null;

    const years = req?.cycleLengthYears ?? 0;
    let nextStart = cyclestart;
    let nextEnd = cycleForm.cycleend;

    if (req?.hasFixedCycleDates && req.fixedCycleStartMonth && req.fixedCycleStartDay) {
      const now = new Date();
      const cy = now.getFullYear();
      nextStart = new Date(cy, req.fixedCycleStartMonth - 1, req.fixedCycleStartDay)
        .toISOString().split('T')[0];
      if (req.fixedCycleEndMonth && req.fixedCycleEndDay) {
        nextEnd = new Date(cy + Math.floor(years), req.fixedCycleEndMonth - 1, req.fixedCycleEndDay)
          .toISOString().split('T')[0];
      }
    } else if (nextStart && years > 0) {
      nextEnd = computeEndDate(nextStart, years);
    }

    setCycleForm(f => ({
      ...f,
      professionID,
      cyclestart: nextStart,
      cycleend: nextEnd,
      cyclename: f.cyclename || buildCycleName(professionID, stateid, nextStart),
    }));
  };

  const handleCycleStateChange = async (stateid: string) => {
    const { professionID, cyclestart } = cycleForm;
    await fetchRequirement(professionID, stateid);
    const req = reqCacheRef.current[`${professionID}:${stateid}`] ?? null;
    setStateReq(req);

    const years = req?.cycleLengthYears ?? 0;
    let nextStart = cyclestart;
    let nextEnd = cycleForm.cycleend;

    if (req?.hasFixedCycleDates && req.fixedCycleStartMonth && req.fixedCycleStartDay) {
      const now = new Date();
      const cy = now.getFullYear();
      nextStart = new Date(cy, req.fixedCycleStartMonth - 1, req.fixedCycleStartDay)
        .toISOString().split('T')[0];
      if (req.fixedCycleEndMonth && req.fixedCycleEndDay) {
        nextEnd = new Date(cy + Math.floor(years), req.fixedCycleEndMonth - 1, req.fixedCycleEndDay)
          .toISOString().split('T')[0];
      }
    } else if (nextStart && years > 0) {
      nextEnd = computeEndDate(nextStart, years);
    }

    setCycleForm(f => ({
      ...f,
      stateid,
      cyclestart: nextStart,
      cycleend: nextEnd,
      cyclename: f.cyclename || buildCycleName(professionID, stateid, nextStart),
    }));
  };

  const handleCycleStartChange = (cyclestart: string) => {
    const years = stateReq?.cycleLengthYears ?? 0;
    const cycleend = years > 0 ? computeEndDate(cyclestart, years) : cycleForm.cycleend;
    setCycleForm(f => ({ ...f, cyclestart, cycleend }));
  };

  const addCycle = async () => {
    setCycleError('');
    if (!cycleForm.cyclename.trim()) { setCycleError('Cycle name is required.'); return; }
    if (!cycleForm.professionID) { setCycleError('Profession is required.'); return; }
    if (!cycleForm.stateid) { setCycleError('State is required.'); return; }
    if (!cycleForm.cyclestart || !cycleForm.cycleend) { setCycleError('Start and end dates are required.'); return; }
    if (new Date(cycleForm.cycleend) <= new Date(cycleForm.cyclestart)) {
      setCycleError('End date must be after start date.');
      return;
    }

    setCycleAdding(true);
    try {
      const res = await api.post(`/api/company/members/${memberId}/cycles`, {
        cyclename: cycleForm.cyclename.trim(),
        professionID: cycleForm.professionID,
        stateid: Number(cycleForm.stateid),
        cyclestart: cycleForm.cyclestart,
        cycleend: cycleForm.cycleend,
      });
      setCycles(prev => [res.data, ...prev]);
      setShowAddCycle(false);
      setCycleForm(EMPTY_CYCLE_FORM);
    } catch (e: any) {
      setCycleError(e.response?.data?.error || 'Failed to create cycle.');
    } finally {
      setCycleAdding(false);
    }
  };

  if (loading) return <div className="container mt-5 text-center"><div className="spinner-border text-primary" role="status" /></div>;
  if (error) return <div className="container mt-4"><div className="alert alert-danger">{error}</div></div>;
  if (!member) return <div className="container mt-4"><div className="alert alert-warning">Member not found.</div></div>;

  const u = member.user;
  const displayName = u ? [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email : member.inviteEmail || '—';
  const isPending = member.inviteStatus === 'pending';

  return (
    <div className="container mt-4" style={{ maxWidth: 800 }}>

      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h4 className="mb-0">{displayName}</h4>
          <p className="text-muted small mb-0">{u?.email || member.inviteEmail} · {member.memberType} member</p>
        </div>
        <Link to="/team" className="btn btn-outline-secondary btn-sm">← Team Dashboard</Link>
      </div>

      {/* Pending invite banner */}
      {isPending && (
        <div className="alert alert-warning d-flex justify-content-between align-items-center mb-4">
          <div>
            <strong>Invite Pending</strong> — {displayName} hasn't set their password yet.
          </div>
          <button className="btn btn-warning btn-sm" onClick={resendInvite} disabled={resending}>
            {resending ? 'Sending…' : 'Resend Invite Email'}
          </button>
        </div>
      )}

      {/* Member info card */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-sm-4">
              <small className="text-muted d-block">Email</small>
              <span>{u?.email || member.inviteEmail || '—'}</span>
            </div>
            <div className="col-sm-4">
              <small className="text-muted d-block">Profession</small>
              {editingProfession ? (
                <div className="d-flex align-items-center gap-2 mt-1">
                  <select
                    className="form-select form-select-sm"
                    style={{ width: 'auto' }}
                    value={professionDraft}
                    onChange={e => setProfessionDraft(e.target.value)}
                    autoFocus
                  >
                    <option value="">Select…</option>
                    {professions.map(p => (
                      <option key={p.professionID} value={p.professionID}>{p.professionID} – {p.name}</option>
                    ))}
                  </select>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={saveProfession}
                    disabled={professionSaving || !professionDraft}
                  >
                    {professionSaving ? '…' : 'Save'}
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setEditingProfession(false)}
                    disabled={professionSaving}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="d-flex align-items-center gap-2">
                  <span>{u?.professionID || licenses[0]?.professionID || <span className="text-warning fw-semibold">Not set</span>}</span>
                  <button
                    className="btn btn-outline-secondary btn-sm py-0 px-2"
                    style={{ fontSize: '0.7rem' }}
                    onClick={openProfessionEdit}
                  >
                    Edit
                  </button>
                </div>
              )}
            </div>
            <div className="col-sm-4">
              <small className="text-muted d-block">Licenses</small>
              {licenses.length
                ? licenses.map((lic, i) => {
                    const stateName = states.find(s => s.stateID === lic.stateID)?.state || `State ${lic.stateID}`;
                    return (
                      <span key={i} className="badge bg-light text-dark border me-1">
                        {lic.professionID} · {stateName}
                      </span>
                    );
                  })
                : <span className="text-muted">—</span>
              }
            </div>
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="d-flex gap-2 mb-4 flex-wrap">
        {u?._id && (
          <>
            <Link to={`/admin/compliance-report/${u._id}?context=team`} className="btn btn-outline-secondary btn-sm">
              View Compliance Report
            </Link>
            <Link to="/team/session/new" className="btn btn-success btn-sm">
              + Add Firm Session (Bulk)
            </Link>
          </>
        )}
      </div>

      {/* Licenses */}
      <div className="card shadow-sm mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h6 className="mb-0">Licenses ({licenses.length})</h6>
          <button className="btn btn-primary btn-sm" onClick={openAddLicense}>+ Add License</button>
        </div>
        {licenses.length === 0 ? (
          <div className="card-body text-muted small">No licenses on file. Add a license to enable compliance tracking.</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th>Profession</th>
                  <th>State</th>
                  <th>License #</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {licenses.map(lic => (
                  <tr key={lic._id}>
                    <td className="fw-medium">{lic.professionID}</td>
                    <td className="small text-muted">
                      {states.find(s => s.stateID === lic.stateID)?.state || `State ${lic.stateID}`}
                    </td>
                    <td className="small">{lic.licenseNumber}</td>
                    <td>
                      <span className={`badge ${lic.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>
                        {lic.status}
                      </span>
                    </td>
                    <td className="text-end">
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        style={{ fontSize: '0.75rem' }}
                        onClick={() => openEditLicense(lic)}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Cycles */}
      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h6 className="mb-0">Compliance Cycles ({cycles.length})</h6>
          <button className="btn btn-primary btn-sm" onClick={openAddCycle}>
            + Add Cycle
          </button>
        </div>

        {cycles.length === 0 ? (
          <div className="card-body text-muted">No cycles found for this member.</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th>Cycle Name</th>
                  <th>Profession</th>
                  <th>Period</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {cycles.map(c => {
                  const now = new Date();
                  const end = c.cycleend ? new Date(c.cycleend) : null;
                  const start = c.cyclestart ? new Date(c.cyclestart) : null;
                  const isActive = end && end >= now;
                  return (
                    <tr key={c._id}>
                      <td className="fw-medium">
                        {u?._id ? (
                          <Link to={`/cycles/${c._id}?userId=${u._id}&memberId=${memberId}`} style={{ textDecoration: 'none' }}>
                            {c.cyclename || `Cycle ${c.cycleid}`}
                          </Link>
                        ) : (
                          c.cyclename || `Cycle ${c.cycleid}`
                        )}
                      </td>
                      <td className="small text-muted">{c.professionID || '—'}</td>
                      <td className="small text-muted">
                        {start ? start.toLocaleDateString() : '—'}
                        {' – '}
                        {end ? end.toLocaleDateString() : '—'}
                      </td>
                      <td>
                        <span className={`badge ${isActive ? 'bg-success' : 'bg-secondary'}`}>
                          {isActive ? 'Active' : 'Completed'}
                        </span>
                      </td>
                      <td className="text-end">
                        <button
                          className="btn btn-outline-danger btn-sm"
                          style={{ fontSize: '0.75rem' }}
                          disabled={deletingId === c._id}
                          onClick={() => deleteCycle(c._id, c.cyclename || `Cycle ${c.cycleid}`)}
                        >
                          {deletingId === c._id ? '…' : 'Delete'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-muted small mt-3">
        To add classes to a cycle, click the cycle name above to open it, then use the class list to add or remove classes.
        Use "Add Firm Session" to bulk-assign a class across multiple members at once.
      </p>

      {/* License Modal */}
      {showLicenseModal && (
        <div
          className="modal show d-block"
          style={{ background: 'rgba(0,0,0,0.4)' }}
          onClick={e => { if (e.target === e.currentTarget) setShowLicenseModal(false); }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {licenseMode === 'add' ? 'Add License' : 'Edit License'}
                </h5>
                <button className="btn-close" onClick={() => setShowLicenseModal(false)} />
              </div>
              <div className="modal-body">
                <LicenseForm
                  states={states}
                  professions={professions}
                  initialData={editingLicense || { professionID: member?.user?.professionID || '' }}
                  mode={licenseMode}
                  loading={licenseLoading}
                  onSubmit={handleLicenseSubmit}
                  onCancel={() => setShowLicenseModal(false)}
                  onDelete={licenseMode === 'edit' && editingLicense ? () => deleteLicense(editingLicense) : undefined}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Cycle Modal */}
      {showAddCycle && (
        <div
          className="modal show d-block"
          style={{ background: 'rgba(0,0,0,0.4)' }}
          onClick={e => { if (e.target === e.currentTarget) setShowAddCycle(false); }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Cycle for {displayName}</h5>
                <button className="btn-close" onClick={() => setShowAddCycle(false)} />
              </div>
              <div className="modal-body">
                {cycleError && <div className="alert alert-danger py-2 small">{cycleError}</div>}

                <div className="mb-3">
                  <label className="form-label">Cycle Name <span className="text-danger">*</span></label>
                  <input
                    className="form-control"
                    placeholder="e.g. NY Architecture 2024–2026"
                    value={cycleForm.cyclename}
                    onChange={e => setCycleForm(f => ({ ...f, cyclename: e.target.value }))}
                    autoFocus
                  />
                </div>

                <div className="row g-3 mb-3">
                  <div className="col">
                    <label className="form-label">Profession <span className="text-danger">*</span></label>
                    <select
                      className="form-select"
                      value={cycleForm.professionID}
                      onChange={e => handleCycleProfessionChange(e.target.value)}
                    >
                      <option value="">Select profession</option>
                      {professions.map(p => (
                        <option key={p.professionID} value={p.professionID}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col">
                    <label className="form-label">State <span className="text-danger">*</span></label>
                    <select
                      className="form-select"
                      value={cycleForm.stateid}
                      onChange={e => handleCycleStateChange(e.target.value)}
                    >
                      <option value="">Select state</option>
                      {states.map(s => (
                        <option key={s.stateID} value={s.stateID}>{s.state}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {stateReq && (
                  <div className="alert alert-info py-2 small mb-3">
                    {stateReq.totalCreditsRequired} credits required ·{' '}
                    {stateReq.cycleLengthYears}-year cycle
                    {stateReq.hasFixedCycleDates && ' · Fixed dates'}
                  </div>
                )}

                <div className="row g-3">
                  <div className="col">
                    <label className="form-label">Start Date <span className="text-danger">*</span></label>
                    <input
                      type="date"
                      className="form-control"
                      value={cycleForm.cyclestart}
                      onChange={e => handleCycleStartChange(e.target.value)}
                    />
                  </div>
                  <div className="col">
                    <label className="form-label">End Date <span className="text-danger">*</span></label>
                    <input
                      type="date"
                      className="form-control"
                      value={cycleForm.cycleend}
                      onChange={e => setCycleForm(f => ({ ...f, cycleend: e.target.value }))}
                    />
                    {stateReq && stateReq.cycleLengthYears > 0 && (
                      <div className="form-text">Auto-filled from start date + {stateReq.cycleLengthYears} years</div>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setShowAddCycle(false)}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={addCycle}
                  disabled={cycleAdding}
                >
                  {cycleAdding ? 'Creating…' : 'Create Cycle'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamMemberManage;
